import React, { useState, useEffect, useCallback, useRef } from "react";
import { GameOverType, GTVMessage, PlayerType } from "../../shared/util/types";
import { AllPlayersInType, NewMemeType, NextRoundType } from "./types";
import Instructions from "../../shared/components/Instructions";
import ImageAndCaptions from "../../shared/components/ImageAndCaptions";
import ShowScores from "../../shared/components/ShowScores";
import { Box } from "@mantine/core";
import { DEFAULT_THEME } from "../../shared/util/consts";

const MemerableGame = () => {
  const [players, setPlayers] = useState<PlayerType[]>([]);
  const theme = DEFAULT_THEME;
  const sendHostMessage = useCallback(async (msg: string) => {}, []);
  const [judge, setJudge] = useState("");
  const [round, setRound] = useState("Round 1");
  const [instructions, setInstructions] = useState("");
  const [reminder, setReminder] = useState("");
  const [state, setState] = useState("NextRound"); // Should be NextRound to start
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<AllPlayersInType | undefined>(
    undefined
  );

  const [gameOverData, setGameOverData] = useState<GameOverType | null>(null);
  const setPlayersUnsubmitted = useCallback(() => {
    setPlayers(
      players.map((p) => {
        return {
          ...p,
          Submitted: false,
        };
      })
    );
  }, [setPlayers, players]);
  const onLoaded = async () => {
    await sendHostMessage("GameStarted");
  };
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      onLoaded();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("Meme Processing msg", msg);
      if (!msg) {
        return false;
      }
      switch (msg.Type) {
        case "AddPlayer":
          const newPlayer = msg.Payload as PlayerType;
          setPlayers([...players, { ...newPlayer, Submitted: true }]);
          break;
        case "NextRound":
          setState(msg.Type);
          const getPrompt: NextRoundType = msg.Payload as NextRoundType;
          setRound(getPrompt.Round);
          setJudge(getPrompt.Judge);
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Submitted: p.Name === judge,
                Score: getPrompt.Points[p.Name],
              };
            })
          );
          setInstructions(getPrompt.Instructions);
          setReminder(getPrompt.Reminder);
          await sendHostMessage("RoundStarted");
          break;
        case "NewMeme":
          setState(msg.Type);
          const newMeme: NewMemeType = msg.Payload as NewMemeType;
          setRound("Round " + newMeme.Round);
          setImageUrl(newMeme.MemeURL);
          setInstructions(newMeme.Instructions);
          setReminder(newMeme.Reminder);
          setPlayersUnsubmitted();
          await sendHostMessage("MemeDisplaying");
          break;
        case "OnePlayerIn":
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Submitted:
                  p.Name === (msg.Payload as string) ? true : p.Submitted,
              };
            })
          );
          break;
        case "AllPlayersIn":
          setState(msg.Type);
          const results: AllPlayersInType = msg.Payload as AllPlayersInType;
          setInstructions(results.Instructions);
          setReminder(results.Reminder);
          setResults(results);
          break;
        case "GameOver":
          setState(msg.Type);
          const gameOver: GameOverType = msg.Payload as GameOverType;
          setGameOverData(gameOver);
          return true;
        default:
          console.log("Unimplemented message:", msg);
          break;
      }
      return false;
    },
    [players, sendHostMessage, setPlayers, setPlayersUnsubmitted, judge]
  );
  // TODO
  useEffect(() => {
    handleMessage({ Type: "TestMessage", Payload: {} } as GTVMessage);
  }, [handleMessage]);
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "calc(100vh)",
        justifyContent: "space-between",
      }}
    >
      {state === "NextRound" ? (
        <Instructions
          theme={theme}
          judge={judge}
          instructions={instructions}
          players={players}
          imageSrc={imageUrl}
          reminder={reminder}
          round={round}
        />
      ) : state === "NewMeme" ? (
        <Instructions
          theme={theme}
          judge={judge}
          instructions={instructions}
          players={players}
          imageSrc={imageUrl}
          reminder={reminder}
          round={round}
        />
      ) : state === "AllPlayersIn" && results !== undefined ? (
        <ImageAndCaptions
          captions={results.Submissions.slice(0, 3).map((s) => s.Text)}
          imageUrl={imageUrl || ""}
          theme={theme}
          judge={judge}
          round={round}
          instructions={instructions}
          players={players}
          reminder={reminder}
        />
      ) : state === "GameOver" && gameOverData ? (
        <ShowScores players={players} onRenderComplete={async () => {
          await sendHostMessage("ShowResultsComplete");
        }} />
      ) : null}
    </Box>
  );
};

export default MemerableGame;
